import React, { FC, useCallback, useEffect, useState } from 'react';
import { Checkbox, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select } from '@material-ui/core';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import { Skeleton } from '@material-ui/lab';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  className?: any;
  label?: string;
  onChange?: (value?: number | number[]) => void;
}

interface SelectProps {
  value?: number;
  multiple?: false;
  onChange?: (value?: number) => void;
}

interface MultiSelectProps {
  value?: number[];
  multiple: true;
  onChange?: (value?: number[]) => void;
}

type EmployeeSelectProps = Props & (SelectProps | MultiSelectProps);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const LegacyEmployeeSelect: FC<EmployeeSelectProps> = React.forwardRef<any, EmployeeSelectProps>(
  ({ onChange, multiple, label, disabled, value, className, helperText, ...props }, ref) => {
    const [employees, setEmployees] = useState<LegacyEmployee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const { fetch } = useAuth();

    const fetchEmployees = (): Promise<LegacyEmployee[]> => {
      const queryURL = '/api/employee/';
      const graphQL = '{ employees{ id, firstname, lastname } }';

      return fetch(queryURL + graphQL, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data['data']['employees']) {
            return data['data']['employees'];
          }

          throw new Error('Aucun employé trouvé ...');
        });
    };

    const getEmployee = useCallback((employeeId: number) => employees.find((employee) => employee.id === employeeId), [
      employees,
    ]);

    const getEmployeeLabel = useCallback(
      (employeeId: number) => {
        const employee = getEmployee(employeeId);
        return `${employee?.firstname} ${employee?.lastname}`;
      },
      [employees]
    );

    useEffect(() => {
      fetchEmployees()
        .then((employees) => {
          setEmployees(employees);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }, []);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      if (onChange) {
        const newValue = event.target.value;

        if (multiple) {
          onChange(newValue as number[]);
        } else {
          onChange(newValue as number);
        }
      }
    };

    const handleSortEmployee = (employeeA: LegacyEmployee, employeeB: LegacyEmployee) => {
      return `${employeeA.firstname} ${employeeA.lastname}`.localeCompare(
        `${employeeB.firstname} ${employeeB.lastname}`
      );
    };

    const renderEmployeeValue = useCallback(
      (selected: unknown) =>
        employees
          .filter((employee) => (selected as number[]).includes(employee.id))
          .map((employee) => `${employee.firstname} ${employee.lastname}`)
          .join(', '),
      [employees]
    );

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <FormControl className={className} ref={ref} variant='outlined' size='small' error={props.error} fullWidth>
        <InputLabel>{label || 'Employé' + (multiple ? '(s)' : '')}</InputLabel>
        <Select
          value={value || ''}
          multiple={multiple}
          label={label || 'Employé' + (multiple ? '(s)' : '')}
          onChange={handleChange}
          MenuProps={MenuProps}
          disabled={disabled}
          renderValue={renderEmployeeValue}
        >
          {employees.sort(handleSortEmployee).map((employee) => (
            <MenuItem key={employee.id} value={employee.id}>
              {Array.isArray(value) && <Checkbox checked={value.some((e) => employee.id === e)} />}
              <ListItemText primary={getEmployeeLabel(employee.id)} />
            </MenuItem>
          ))}
        </Select>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default React.memo(LegacyEmployeeSelect);
